import { ApostleBountyFilterInput } from '@apeiron/apostle'
import { SlideFilter } from '@apeiron/common'
import { MayNull } from '@apeiron/temp'
import { styled } from '@mui/material'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchBountyFilterJson from '@src/hooks/json/useFetchBountyFilterJson'
import { BountyFilterData } from '@src/types/bounty'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Form = styled(BlurForm)`
  > *:not(:first-child) {
    margin-top: 16px;
  }
`

const FILTER_FIELDS = [
  'minSkillLevelLowerBound',
  'minSkillLevelUpperBound',
  'minIVLowerBound',
  'minIVUpperBound'
] as const

const BountyOtherFilterSection: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { data } = useFetchBountyFilterJson()

  const [filterData, setFilterData] = useState<MayNull<BountyFilterData>>(null)

  useEffect(() => {
    setFilterData(data)
  }, [data])

  const { t } = useTranslation()

  const numberOfFilters = useMemo(() => {
    const countFilters = R.pipe<any, any, Object, unknown[][], number>(
      R.reject(R.isNil),
      R.pick(FILTER_FIELDS),
      R.toPairs,
      R.length
    )

    const numberOfSlotFilters = countFilters(filterInput)

    return numberOfSlotFilters
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(FILTER_FIELDS, filterInput))
  }, [filterInput, onFilterChange])

  const handleOnValueChange = useCallback(
    (filter: ApostleBountyFilterInput) => {
      onFilterChange(filter)
    },
    [onFilterChange]
  )

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('common.others')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        <Form>
          {filterData &&
            R.map(
              (filter: SlideFilter) => (
                <RangeSlider
                  decimalScale={filter.decimalScale}
                  filterOptions={filterInput}
                  key={filter.name}
                  labelIconURL={filter.image}
                  labelText={filter.name}
                  marks={false}
                  max={filter.max.slot}
                  maxValueKey={filter.max.field}
                  min={filter.min.slot}
                  minValueKey={filter.min.field}
                  step={filter.step ? filter.step : undefined}
                  onFilterOptionsChange={handleOnValueChange}
                />
              ),
              filterData.others
            )}
        </Form>
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: ApostleBountyFilterInput
  onFilterChange: (input: ApostleBountyFilterInput) => void
}

export default BountyOtherFilterSection
